import { useState } from "react";
import api from "../../lib/api";
import { X, CheckCircle2, AlertCircle, RefreshCw, Sparkles, Clock, User } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  OPS_STATUS_OPTIONS,
  getOpsStatusBadgeClass,
  getOpsStatusDotColor,
} from "./opsStatusHelpers";

export default function OpsStatusDetailModal({ conversation, onClose, onSaved }) {
  const currentEval = conversation?.evaluation || {};
  const [status, setStatus] = useState(
    currentEval.status || conversation?.ops_status || "Pending"
  );
  const [remarks, setRemarks] = useState(currentEval.remarks || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!conversation?.id) return;

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await api.post(
        `/ai/chat-history/conversations/${conversation.id}/evaluation`,
        {
          status,
          remarks: remarks.trim() || null,
        }
      );

      if (res.data?.success) {
        const updatedEval = res.data.evaluation || {
          conversation_id: conversation.id,
          status,
          remarks: remarks.trim() || null,
          evaluated_at: new Date().toISOString().replace("T", " ").substring(0, 19),
        };
        setSuccessMsg("Evaluation saved successfully!");
        setTimeout(() => {
          onSaved(updatedEval);
        }, 600);
      } else {
        setError(res.data?.message || "Failed to update evaluation");
      }
    } catch (err) {
      console.error("Evaluation update error:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save evaluation"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-up"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
      data-testid="ops-status-detail-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-slate-900 text-white px-5 py-3.5 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#6E3667] flex items-center justify-center text-white shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold tracking-tight text-white flex items-center gap-2">
                <span>Ops Evaluation</span>
                <span className="text-slate-400 font-normal">#{conversation.id}</span>
              </h3>
              <p className="text-[11px] text-slate-300 truncate max-w-xs sm:max-w-sm">
                {conversation.title || "AI Conversation Session"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4">
          {/* Previous Evaluation Info Banner */}
          {currentEval.evaluator_name || currentEval.evaluated_at ? (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-[#6E3667] shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-semibold text-slate-800">Latest Review Record</span>
                <p className="text-[11px] text-slate-500">
                  {currentEval.evaluator_name && (
                    <>
                      By <strong className="text-slate-700">{currentEval.evaluator_name}</strong>
                    </>
                  )}
                  {currentEval.evaluator_mail && !currentEval.evaluator_name && (
                    <>
                      By <strong className="text-slate-700">{currentEval.evaluator_mail}</strong>
                    </>
                  )}
                  {currentEval.evaluated_at && ` on ${currentEval.evaluated_at}`}
                </p>
              </div>
            </div>
          ) : null}

          {/* Status Selection */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
              Ops Evaluation Status
            </label>
            <div className="relative">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#6E3667] focus:border-[#6E3667] cursor-pointer"
              >
                <option value="Pending">🟡 Pending</option>
                <option value="Under review">🔵 Under review</option>
                <option value="Converted">🟢 Converted</option>
                <option value="valid response">🟢 valid response</option>
                <option value="Improve - KB - RD">🟠 Improve - KB - RD</option>
              </select>
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="text-[11px] text-slate-500">Active status preview:</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border",
                  getOpsStatusBadgeClass(status)
                )}
              >
                <span className={cn("w-1.5 h-1.5 rounded-full", getOpsStatusDotColor(status))} />
                <span>{status}</span>
              </span>
            </div>
          </div>

          {/* Remarks Textarea */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
              Evaluation Remarks / Action Taken
            </label>
            <textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter notes, root cause, or adjustments needed (e.g., 'Accurate response for pricing enquiry' or 'Update KB for pickup timings')..."
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#6E3667] focus:border-[#6E3667] resize-none"
            />
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
              <span>{error}</span>
            </div>
          )}

          {/* Success Toast Banner */}
          {successMsg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-lg text-xs flex items-center gap-2 animate-fade-in font-medium">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 bg-[#6E3667] hover:bg-[#592a53] text-white text-xs font-semibold rounded-lg shadow-xs transition-colors shrink-0 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Evaluation</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
